(ns arthur.ui.drag
  "What a drag out of the pool is carrying while it is in flight, and what it
  would look like where it lands.

  A PAGE-LOCAL ATOM, not `dataTransfer`. A browser lets a drop target read the
  payload on `drop` and hides it on every `dragover` before that, which is exactly
  when a preview needs it — so the pool writes the payload here on `dragstart`,
  and the `text/plain` it also sets is only what makes the drag a drag. A plain
  atom rather than app-db because it lives for one gesture and nothing renders
  from it; what the preview DOES render from — the frame and point under the
  pointer — is `[:ui :drop]`, set by `::ui/drop-hover`."
  (:require [arthur.domain.clip :as clip]
            [arthur.domain.palette :as pal]
            [arthur.events.footage :as footage]
            [arthur.events.project :as project]
            [arthur.events.ui :as ui]
            [arthur.footage.store :as store]
            [re-frame.core :as rf]))

(defonce carrying (atom nil))

(defn- outline
  "Frame 0 of symbol `sid` as plain shapes in its own space, for the stage's
  preview. Frame 0 because an instance dropped at the playhead starts there."
  [document st sid]
  (vec (keep (fn [op]
               (case (:kind op)
                 :poly {:kind :poly :pts (vec (take (* 2 (:n op)) (array-seq (:pts op))))}
                 :disc (select-keys op [:kind :cx :cy :r])
                 :rect (select-keys op [:kind :cx :cy :size])
                 nil))
             ((clip/resolver document sid st pal/index-of nil) 0))))

(defn symbol!
  "Start carrying symbol `sid` of the loaded document into the open symbol."
  [clip-id sid open]
  (let [{document :clip st :store} (store/entry clip-id)]
    (reset! carrying (merge {:kind :symbol :sid sid
                             :label (clip/symbol-name document sid)
                             :frames (clip/frames document sid)
                             ;; A symbol cannot go inside itself or inside
                             ;; anything it places. Refused by not ACCEPTING the
                             ;; drop, so the pointer says so while it hovers.
                             :refused? (clip/contains-symbol? document sid open)
                             ;; The same middle `clip/place-symbol` will anchor
                             ;; on, so the preview is where the drop lands.
                             :center (clip/center document st sid)
                             :shapes (outline document st sid)}))))

(defn accepts?
  "Whether the stage or the tracks should accept what is being carried: things
  out of the pool, and not one that would make a cycle."
  []
  (let [c @carrying]
    (and c (#{:symbol :footage :import :sound :tracing} (:kind c)) (not (:refused? c)))))

(defn row!
  "Start carrying the timeline row at `path` — a node of kind `node-kind`, to be
  moved into another symbol or grouped with another node."
  [path node-kind selection & [selections in]]
  (reset! carrying {:kind :row :path path :node-kind node-kind
                    :selection selection :selections selections :in in}))

(defn row
  "The path of the row being carried, or nil when it is not a row."
  []
  (let [c @carrying] (when (= :row (:kind c)) (:path c))))

(defn row-kind
  "The node kind of the row being carried."
  []
  (:node-kind @carrying))

(defn row-selection []
  (when (= :row (:kind @carrying)) (:selection @carrying)))

(defn row-selections []
  (when (= :row (:kind @carrying)) (:selections @carrying)))

(defn row-in [] (:in @carrying))

(defn other!
  "Start carrying something that is not yet in the document: `:kind` says what,
  and the rest is what a preview can show of it before it is fetched."
  [payload]
  (reset! carrying payload))

(defn done!
  "The gesture is over, dropped or abandoned."
  []
  (reset! carrying nil)
  (rf/dispatch [::ui/drop-clear]))

(defn hover!
  "Say where the drag would land, for the previews. `point` is nil over the
  timeline."
  ([where frame point] (hover! where frame point nil))
  ([where frame point target]
  (when-let [{:keys [kind label frames]} @carrying]
    (rf/dispatch [::ui/drop-hover {:where where :frame frame :point point
                                   :label label :frames frames
                                   :target target
                                   :sound? (= :sound kind)}]))))

(defn pos-for
  "Where the preview goes so the symbol's middle is under `point` — what
  `clip/place-symbol` will do on the drop."
  [point]
  (mapv - point (or (:center @carrying) [0 0])))

(defn land!
  "Drop what is being carried at `frame` of the open symbol: with its middle on
  stage pixel `point`, or, with no point — the timeline — where it was drawn."
  ([frame point] (land! frame point nil))
  ([frame point target]
  (when-let [{:keys [kind sid] :as c} (when (accepts?) @carrying)]
    (case kind
      :symbol  (rf/dispatch [::ui/drop-symbol sid frame point target])
      ;; Video is asked about before anything happens: which frames, and what
      ;; the symbol they become is called. The explicit timeline row travels
      ;; with the question, so the answer lands where the drop pointed.
      :footage (rf/dispatch [::footage/ask-convert c frame point target])
      :import  (rf/dispatch [::project/import c frame point target])
      ;; Where it is dropped in time; a sound has no place in space.
      :sound   (rf/dispatch [::ui/drop-sound c frame target])
      ;; A still to trace over, made into a tracing symbol where it lands.
      :tracing (rf/dispatch [::ui/drop-tracing (:symbol c) frame point target])
      nil))
  (done!)))
